import { useEffect, useRef } from 'react'
import { ExternalLink, Check } from 'lucide-react'
import SectionHeading from './SectionHeading'
import FlowDiagram from './FlowDiagram'
import { PROJECT_CATALYST } from '../data/projects'
import { initGSAP, prefersReducedMotion } from '../lib/motion'

const STORY: { num: string; title: string; body: string }[] = [
  { num: '01', title: 'The problem', body: PROJECT_CATALYST.problem },
  { num: '02', title: 'Why it is hard', body: PROJECT_CATALYST.whyHard },
  { num: '03', title: 'The solution', body: PROJECT_CATALYST.solution },
]

const CatalystCaseStudy: React.FC<{ standalone?: boolean }> = ({
  standalone = false,
}) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (prefersReducedMotion()) return
    const gsapApi = initGSAP()
    const ctx = gsapApi.context(() => {
      gsapApi.fromTo(
        '[data-catalyst]',
        { opacity: 0, y: 24 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power2.out',
          stagger: 0.08,
          scrollTrigger: {
            trigger: ref.current,
            start: standalone ? 'top 92%' : 'top 75%',
            once: true,
          },
        }
      )
    }, ref)
    return () => ctx.revert()
  }, [standalone])

  return (
    <section
      id={standalone ? undefined : 'projects'}
      ref={ref}
      className={standalone ? 'pt-8 pb-20 sm:pb-28' : 'section-padding'}
    >
      <div className="container-width">
        <div data-catalyst>
          <SectionHeading
            label="Case study"
            meta="01 · self-service platform"
            title={PROJECT_CATALYST.title}
            description={PROJECT_CATALYST.tagline}
          />
        </div>

        <div data-catalyst>
          <div className="flex items-center gap-3 mb-8 flex-wrap">
            <span className="mono-label !text-primary-500">
              {PROJECT_CATALYST.kind}
            </span>
            <span className="hidden sm:block w-px h-4 bg-neutral-300 dark:bg-white/15" />
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400">
              {PROJECT_CATALYST.role}
            </span>
            <a
              href={PROJECT_CATALYST.repo}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto inline-flex items-center gap-2 btn-secondary !py-2 !px-4 text-sm"
            >
              View repository
              <ExternalLink size={14} />
            </a>
          </div>

          <div className="grid gap-5 md:grid-cols-3">
            {STORY.map((block) => (
              <div
                key={block.num}
                className="rounded-2xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-neutral-900 p-6"
              >
                <span className="font-mono text-xs text-primary-500">
                  {block.num}
                </span>
                <h3 className="font-display text-lg font-semibold tracking-tight mt-2 mb-3">
                  {block.title}
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                  {block.body}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div data-catalyst className="mt-14">
          <h3 className="font-display text-xl font-semibold tracking-tight mb-6 flex items-center gap-3">
            Request lifecycle
            <span className="mono-label !text-xs !text-neutral-400 dark:!text-neutral-500">
              developer → platform → cloud
            </span>
          </h3>
          <FlowDiagram title={PROJECT_CATALYST.flowTitle} steps={PROJECT_CATALYST.flow} />
        </div>

        <div data-catalyst className="mt-14 grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <h3 className="font-display text-xl font-semibold tracking-tight mb-4">
              The platform
            </h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
              The services that sit alongside Kubernetes and make the IDP
              operate like a product rather than a cluster.
            </p>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2">
            {PROJECT_CATALYST.platform.map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 rounded-xl border border-neutral-200 dark:border-white/8 px-4 py-3 text-sm text-neutral-700 dark:text-neutral-200"
              >
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div data-catalyst className="mt-14">
          <h3 className="font-display text-xl font-semibold tracking-tight mb-6 flex items-center gap-3">
            Key decisions
            <span className="mono-label !text-xs !text-neutral-400 dark:!text-neutral-500">
              trade-offs
            </span>
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PROJECT_CATALYST.challenges.map((c, i) => (
              <div
                key={c.title}
                className="group rounded-2xl border border-neutral-200 dark:border-white/10 p-5 transition-colors duration-200 hover:border-primary-500/30"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="font-mono text-[11px] text-neutral-400 dark:text-neutral-500">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h4 className="font-display text-base font-semibold tracking-tight">
                    {c.title}
                  </h4>
                </div>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                  {c.decision}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div
          data-catalyst
          className="mt-14 rounded-3xl bg-neutral-950 dark:bg-neutral-900 border border-neutral-800 dark:border-white/10 p-8 sm:p-12 text-white"
        >
          <span className="mono-label !text-primary-400">Outcomes</span>
          <h3 className="font-display text-2xl font-semibold tracking-tight mt-3 mb-8">
            What shipping it actually changed
          </h3>
          <ul className="grid gap-4 sm:grid-cols-2">
            {PROJECT_CATALYST.results.map((result) => (
              <li
                key={result}
                className="flex items-start gap-3 text-sm text-neutral-300 leading-relaxed"
              >
                <span className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-md bg-primary-500/20 text-primary-400 flex-shrink-0">
                  <Check size={13} />
                </span>
                {result}
              </li>
            ))}
          </ul>
        </div>

        <div data-catalyst className="mt-10 flex flex-wrap gap-2">
          {PROJECT_CATALYST.technologies.map((tech) => (
            <span
              key={tech}
              className="inline-flex items-center rounded-lg border border-neutral-200 dark:border-white/8 px-3 py-1.5 font-mono text-xs text-neutral-600 dark:text-neutral-300"
            >
              {tech}
            </span>
          ))}
        </div>
      </div>
    </section>
  )
}

export default CatalystCaseStudy